// ============================================================
// InvitesPanel — ChartFlow
// ============================================================
// Shows pending collaboration invites for the signed-in user.
// Handles three modes: view, write (fork), linked write.
// Polls every 5s so new invites appear without a page refresh.
// ============================================================

import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useAuthStore } from '../../store/authStore';
import { getPendingInvites, acceptInvite, forkSong } from '../../core/cloudSync';
import { supabase } from '../../lib/supabase';
import { saveSong } from '../../core/library';
import { useLibraryStore } from '../../store/libraryStore';
import type { HChartFile } from '../../core/types';

type ShareMode = 'view' | 'write' | 'linked';

const MODE_LABELS: Record<ShareMode, string> = {
  view:   'View only',
  write:  'Write',
  linked: 'Linked write',
};

const MODE_COLORS: Record<ShareMode, string> = {
  view:   'var(--color-ink-muted)',
  write:  '#2a9d8f',
  linked: 'var(--color-accent)',
};

interface PendingInvite {
  id:         string;
  song_id:    string;
  email:      string;
  role:       'viewer' | 'editor';
  mode:       ShareMode;
  invited_at: string;
  songs:      { title: string } | null;
}

interface InvitesPanelProps {
  onSongLoad: (song: import('../../core/types').Song) => void;
}

export function InvitesPanel({ onSongLoad }: InvitesPanelProps) {
  const user         = useAuthStore((s) => s.user);
  const refreshIndex = useLibraryStore((s) => s.refreshIndex);

  const [invites, setInvites]         = useState<PendingInvite[]>([]);
  const [open, setOpen]               = useState(false);
  const [loading, setLoading]         = useState<string | null>(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, right: 0 });
  const buttonRef                     = useRef<HTMLButtonElement>(null);

  const loadInvites = useCallback(async () => {
    if (!user) return;
    const { data } = await getPendingInvites();
    setInvites((data as unknown as PendingInvite[]) ?? []);
  }, [user]);

  // Initial load
  useEffect(() => { loadInvites(); }, [loadInvites]);

  // FIX: poll every 5s so new invites appear automatically without
  // needing a page refresh. 5s is a good balance — imperceptible delay
  // for the recipient, cheap on Supabase (one lightweight query per poll).
  useEffect(() => {
    if (!user) return;
    const timer = setInterval(loadInvites, 5_000);
    return () => clearInterval(timer);
  }, [user, loadInvites]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handle = (e: MouseEvent) => {
      const target = e.target as Node;
      const dropdown = document.getElementById('invites-dropdown-portal');
      if (
        buttonRef.current && !buttonRef.current.contains(target) &&
        dropdown && !dropdown.contains(target)
      ) setOpen(false);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  const handleToggle = () => {
    if (!open && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPos({ top: rect.bottom + window.scrollY + 6, right: window.innerWidth - rect.right });
    }
    setOpen((v) => !v);
  };

  const handleAccept = useCallback(async (invite: PendingInvite) => {
    if (!user) return;
    setLoading(invite.id);

    const result = await acceptInvite(invite.id);
    if (!result.ok) { setLoading(null); return; }

    if (invite.mode === 'write') {
      const forked = await forkSong(invite.song_id);
      if (forked.ok && forked.song) {
        refreshIndex();
        onSongLoad(forked.song);
      }
    } else {
      const { data, error } = await supabase
        .from('songs')
        .select('data')
        .eq('id', invite.song_id)
        .single();

      if (!error && data?.data) {
        const file = data.data as unknown as HChartFile;
        if (file?.song) {
          saveSong(file.song);
          refreshIndex();
          onSongLoad(file.song);
        }
      }
    }

    setLoading(null);
    setOpen(false);
    loadInvites();
  }, [user, onSongLoad, refreshIndex, loadInvites]);

  const handleDecline = useCallback(async (inviteId: string) => {
    await supabase.from('song_collaborators').delete().eq('id', inviteId);
    loadInvites();
  }, [loadInvites]);

  if (!user || invites.length === 0) return null;

  const count = invites.length;

  const dropdown = open ? ReactDOM.createPortal(
    <div
      id="invites-dropdown-portal"
      style={{
        position: 'absolute',
        top:   dropdownPos.top,
        right: dropdownPos.right,
        width: 320,
        zIndex: 9999,
        background: 'var(--color-paper)',
        border: '1px solid var(--color-rule)',
        borderRadius: 8,
        boxShadow: '0 4px 24px rgba(0,0,0,0.12)',
        padding: '8px 0',
      }}
    >
      <div style={{
        fontSize: 9, fontFamily: 'var(--font-label)',
        letterSpacing: '0.15em', textTransform: 'uppercase',
        color: 'var(--color-ink-muted)', padding: '4px 12px 8px',
      }}>
        Pending invites
      </div>

      {invites.map((invite) => {
        const modeLabel = MODE_LABELS[invite.mode ?? 'view'];
        const modeColor = MODE_COLORS[invite.mode ?? 'view'];
        return (
          <div key={invite.id} style={{ padding: '10px 12px', borderTop: '1px solid var(--color-rule)' }}>
            <div style={{
              fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 15,
              color: 'var(--color-ink-primary)', marginBottom: 4, lineHeight: 1.2,
            }}>
              {invite.songs?.title ?? 'Untitled'}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
              <span style={{
                fontFamily: 'var(--font-mono)', fontSize: 9,
                padding: '2px 6px', borderRadius: 999,
                border: `1px solid ${modeColor}`,
                color: modeColor,
                textTransform: 'uppercase', letterSpacing: '0.08em',
              }}>
                {modeLabel}
              </span>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--color-ink-muted)' }}>
                {new Date(invite.invited_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
              </span>
            </div>

            <div style={{
              fontFamily: 'var(--font-mono)', fontSize: 10,
              color: 'var(--color-ink-muted)', marginBottom: 10,
              lineHeight: 1.4,
            }}>
              {invite.mode === 'view'   && 'You will be able to view this chart.'}
              {invite.mode === 'write'  && 'You will get your own independent copy to edit.'}
              {invite.mode === 'linked' && 'You will edit this chart together in real time.'}
            </div>

            <div style={{ display: 'flex', gap: 6 }}>
              <button
                onClick={() => handleAccept(invite)}
                disabled={loading === invite.id}
                style={{
                  flex: 1, padding: '6px 0',
                  background: 'var(--color-accent)', color: 'white',
                  border: 'none', borderRadius: 5,
                  fontFamily: 'var(--font-mono)', fontSize: 11,
                  cursor: loading === invite.id ? 'wait' : 'pointer',
                  opacity: loading === invite.id ? 0.7 : 1,
                }}
              >
                {loading === invite.id ? '…' : 'Accept'}
              </button>
              <button
                onClick={() => handleDecline(invite.id)}
                disabled={!!loading}
                style={{
                  flex: 1, padding: '6px 0',
                  background: 'transparent', color: 'var(--color-ink-muted)',
                  border: '1px solid var(--color-rule)', borderRadius: 5,
                  fontFamily: 'var(--font-mono)', fontSize: 11, cursor: 'pointer',
                }}
              >
                Decline
              </button>
            </div>
          </div>
        );
      })}
    </div>,
    document.body
  ) : null;

  return (
    <>
      <button
        ref={buttonRef}
        onClick={handleToggle}
        title={`${count} pending invite${count > 1 ? 's' : ''}`}
        style={{
          position: 'relative',
          fontFamily: 'var(--font-mono)', fontSize: 11,
          padding: '4px 10px', borderRadius: 999,
          border: '1px solid var(--color-accent)',
          background: open ? 'var(--color-accent)' : 'rgba(var(--color-accent-rgb), 0.08)',
          color: open ? 'white' : 'var(--color-accent)',
          cursor: 'pointer', transition: 'all 120ms',
        }}
      >
        {count} invite{count > 1 ? 's' : ''}
        <span style={{
          position: 'absolute', top: -2, right: -2,
          width: 7, height: 7, borderRadius: '50%',
          background: 'var(--color-accent)',
          border: '1.5px solid var(--color-paper)',
        }} />
      </button>
      {dropdown}
    </>
  );
}